import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccessPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [retryKey, setRetryKey] = useState(0);
  const [status, setStatus] = useState({
    phase: "checking",
    message: "Confirming your subscription and unlocking access…",
    attempts: 0,
  });
  const sessionId = useMemo(() => searchParams.get("session_id")?.trim() || "", [searchParams]);

  useEffect(() => {
    if (!sessionId) {
      setStatus({
        phase: "error",
        message: "Missing Stripe checkout session. Please return to billing and try again.",
        attempts: 0,
      });
      return undefined;
    }

    let cancelled = false;
    let timerId;

    const pollCheckoutStatus = async (attempt = 1) => {
      if (cancelled) {
        return;
      }

      setStatus({
        phase: "checking",
        message: attempt === 1
          ? "Confirming your subscription and unlocking access…"
          : `Still waiting for Stripe confirmation… (${attempt}/${MAX_ATTEMPTS})`,
        attempts: attempt,
      });

      try {
        const resp = await axios.get(`${API}/stripe/checkout-status/${sessionId}`, { withCredentials: true });
        const data = resp.data || {};

        if (cancelled) {
          return;
        }

        if (data.hasAccess && data.status === "complete") {
          setStatus({
            phase: "success",
            message: "Subscription confirmed. Redirecting you to the dashboard…",
            attempts: attempt,
          });
          timerId = window.setTimeout(() => {
            if (!cancelled) nav("/app");
          }, 900);
          return;
        }

        if (data.status === "expired" || ["failed", "unpaid", "canceled"].includes(data.payment_status)) {
          setStatus({
            phase: "failed",
            message: "We couldn’t confirm this payment. Please review billing and try again.",
            attempts: attempt,
          });
          return;
        }

        if (attempt >= MAX_ATTEMPTS) {
          setStatus({
            phase: "timeout",
            message: "Stripe is taking longer than expected. You can retry the status check or go back to billing.",
            attempts: attempt,
          });
          return;
        }

        timerId = window.setTimeout(() => {
          pollCheckoutStatus(attempt + 1);
        }, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) {
          return;
        }

        if (e?.response?.status === 401) {
          nav("/login");
          return;
        }

        if (attempt >= MAX_ATTEMPTS) {
          setStatus({
            phase: "error",
            message: "We couldn’t verify your checkout session. Please retry or return to billing.",
            attempts: attempt,
          });
          return;
        }

        timerId = window.setTimeout(() => {
          pollCheckoutStatus(attempt + 1);
        }, POLL_INTERVAL_MS);
      }
    };

    pollCheckoutStatus(1);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [nav, retryKey, sessionId]);

  const isRetryVisible = ["failed", "timeout", "error"].includes(status.phase);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="billing-success-page">
      <Card className="w-full max-w-md" data-testid="billing-success-card">
        <CardHeader>
          <CardTitle className="text-lg" data-testid="billing-success-title">Billing successful</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600" data-testid="billing-success-message">{status.message}</p>
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500" data-testid="billing-success-session-id">
            Session: {sessionId || "Unavailable"}
          </div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-400" data-testid="billing-success-attempts">
            Check attempt {Math.max(status.attempts, 1)} of {MAX_ATTEMPTS}
          </div>
          {isRetryVisible ? (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                className="w-full bg-slate-900 hover:bg-slate-800"
                data-testid="billing-success-retry-button"
                onClick={() => setRetryKey((value) => value + 1)}
              >
                Retry status check
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                data-testid="billing-success-back-to-billing-button"
                onClick={() => nav("/billing")}
              >
                Back to billing
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
